import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { api, getServerUrl, setServerUrl } from '../api';
import { useTranslation } from 'react-i18next';
import { usePremium } from '../contexts/PremiumContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAppIcon, AppIcon } from '../contexts/AppIconContext';
import PaywallModal from '../components/PaywallModal';
import { useAuth } from '../contexts/AuthContext';
import { useHousehold } from '../contexts/HouseholdContext';
import { useConfirm } from '../components/ConfirmDialog';
import { usePreferences } from '../contexts/PreferencesContext';

export default function SettingsPage({ onLogout }) {
  const { user, updateUser } = useAuth();
  const { t, i18n } = useTranslation();
  const { isPremium, deactivate: deactivatePremium } = usePremium();
  const { theme, toggle: toggleTheme } = useTheme();
  const { iconKey, icons: appIcons, setIcon } = useAppIcon();
  const [showPaywall, setShowPaywall] = useState(false);
  const { households, activeHousehold, switchHousehold, reload: reloadHouseholds, addHousehold, removeHousehold } = useHousehold();
  const [tab, setTab] = useState('general');
  const scrollRef = useRef(null);
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [tab]);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('ok');
  const [locations, setLocations] = useState([]);
  const [newLoc, setNewLoc] = useState({ name: '', list_type: 'freezer', storage_unit_id: null });
  const [storageUnits, setStorageUnits] = useState([]);
  const [newUnit, setNewUnit] = useState({ name: '', list_type: 'freezer', icon: '' });
  const [pushSub, setPushSub] = useState(null);
  const [pushSupported, setPushSupported] = useState(false);
  const [nativeToken, setNativeToken] = useState(() => localStorage.getItem('push_token'));
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [showChangePw, setShowChangePw] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [newHhName, setNewHhName] = useState('');
  const [newHhIcon, setNewHhIcon] = useState('🏠');
  const [joinCode, setJoinCode] = useState('');
  const [membersMap, setMembersMap] = useState({});
  const { confirm, ConfirmDialog } = useConfirm();

  // ── PAT & server URL state ────────────────────────────────────────────
  const [tokens, setTokens] = useState([]);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState(null);  // vises KUN én gang
  const [serverUrlInput, setServerUrlInput] = useState(() => localStorage.getItem('freshtrack_server_url') || '');
  const isNative = !!window.Capacitor?.isNativePlatform?.();

  const loadTokens = () => api.listTokens().then(setTokens).catch(() => {});
  useEffect(() => { if (tab === 'advanced') loadTokens(); }, [tab]);

  const flash = (m, type = 'ok') => { setMsg(m); setMsgType(type); setTimeout(() => setMsg(''), 4000); };

  useEffect(() => {
    if (isNative) {
      setPushSupported(true);
    } else {
      const pushOk = 'serviceWorker' in navigator && 'PushManager' in window;
      setPushSupported(pushOk);
      if (pushOk) navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription().then(setPushSub));
    }
  }, [isNative]);

  useEffect(() => {
    if (activeHousehold) {
      api.getLocations().then(setLocations).catch(() => {});
      api.getStorageUnits().then(setStorageUnits).catch(() => {});
      api.getHouseholdMembers(activeHousehold.id)
        .then(ms => setMembersMap(prev => ({ ...prev, [activeHousehold.id]: ms })))
        .catch(() => {});
    }
  }, [activeHousehold]);

  useEffect(() => {
    households.forEach(hh => {
      if (hh.role === 'admin') {
        api.getHouseholdMembers(hh.id)
          .then(ms => setMembersMap(prev => ({ ...prev, [hh.id]: ms })))
          .catch(() => {});
      }
    });
  }, [households]);

  const deleteAccount = async () => {
    if (!deletePw) return flash(t('toast.enterPassword'), 'error');
    setDeleting(true);
    try {
      await api.deleteAccount(deletePw);
      onLogout();
    } catch (err) {
      flash(err.message || t('toast.wrongPassword'), 'error');
    } finally { setDeleting(false); }
  };

  const changePassword = async () => {
    if (newPw.length < 6) return flash(t('toast.pwTooShort'), 'error');
    if (newPw !== newPw2) return flash(t('toast.pwMismatch'), 'error');
    try {
      await api.changePassword(currentPw, newPw);
      setCurrentPw(''); setNewPw(''); setNewPw2('');
      setShowChangePw(false);
      flash(t('toast.pwChanged'));
    } catch (err) { flash(err.message || t('toast.pwError'), 'error'); }
  };

  const changeLanguage = async (lang) => {
    i18n.changeLanguage(lang);
    try { await api.updateMe({ language: lang }); } catch {}
  };

  const saveDisplayName = async () => {
    try {
      const updated = await api.updateMe({ display_name: displayName });
      updateUser(updated);
      flash(t('toast.nameSaved'));
    } catch { flash(t('toast.saveError'), 'error'); }
  };

  const subscribePush = async () => {
    if (isNative) {
      try {
        const { requestPushPermission, registerPushToken, getLastPushError } = await import('../native');
        const granted = await requestPushPermission();
        if (!granted) return flash(t('toast.pushDenied'), 'error');
        const token = await registerPushToken();
        if (!token) return flash(t('toast.pushTokenError', { msg: getLastPushError() ?? t('toast.unknownReason') }), 'error');
        const platform = window.Capacitor?.getPlatform?.() || 'unknown';
        await api.registerPushToken(token, platform);
        localStorage.setItem('push_token', token);
        setNativeToken(token);
        flash(t('toast.pushEnabled'));
      } catch (e) { flash(t('toast.pushError'), 'error'); }
    } else {
      const reg = await navigator.serviceWorker.ready;
      const { publicKey } = await api.getVapidKey();
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
      await api.subscribePush(sub.toJSON(), user?.display_name || t('toast.defaultUser'));
      setPushSub(sub); flash(t('toast.pushEnabled'));
    }
  };

  const unsubscribePush = async () => {
    if (isNative) {
      if (!nativeToken) return;
      try { await api.unregisterPushToken(nativeToken); } catch {}
      localStorage.removeItem('push_token');
      setNativeToken(null);
      flash(t('toast.pushDisabled'));
    } else {
      if (!pushSub) return;
      await pushSub.unsubscribe();
      await api.unsubscribePush(pushSub.endpoint);
      setPushSub(null); flash(t('toast.pushDisabled'));
    }
  };

  const createStorageUnit = async () => {
    if (!newUnit.name.trim()) return;
    try {
      await api.createStorageUnit({ ...newUnit });
      const updated = await api.getStorageUnits();
      setStorageUnits(updated);
      setNewUnit({ name: '', list_type: newUnit.list_type, icon: '' });
      flash(t('toast.unitCreated'));
    } catch (err) {
      if (err.code === 'LIMIT_STORAGE_UNITS' || err.message?.includes('LIMIT_STORAGE_UNITS')) {
        setShowPaywall(true);
      } else {
        flash(err.message, 'error');
      }
    }
  };

  const deleteStorageUnit = async (unit) => {
    const ok = await confirm(t('settings.deleteUnitConfirm', { name: unit.name }));
    if (!ok) return;
    try {
      await api.deleteStorageUnit(unit.id, true);
      const updated = await api.getStorageUnits();
      setStorageUnits(updated);
      api.getLocations().then(setLocations).catch(() => {});
      flash(t('toast.unitDeleted'));
    } catch (err) { flash(err.message, 'error'); }
  };

  const addLocation = async () => {
    const unitLocs = locations.filter(l => l.storage_unit_id === newLoc.storage_unit_id);
    const position = unitLocs.length > 0 ? Math.max(...unitLocs.map(l => l.position)) + 1 : 0;
    try {
      await api.addLocation({ ...newLoc, position });
      const [updatedLocs, updatedUnits] = await Promise.all([
        api.getLocations(),
        api.getStorageUnits(),
      ]);
      setLocations(updatedLocs);
      setStorageUnits(updatedUnits);
      setNewLoc({ name: '', list_type: newLoc.list_type, storage_unit_id: null });
      flash(t('toast.drawerAdded'));
    } catch (err) {
      if (err.code === 'LIMIT_LOCATIONS' || err.code === 'LIMIT_UNIT_LOCKED' ||
          err.message?.includes('LIMIT_LOCATIONS')) {
        setShowPaywall(true);
      } else {
        flash(err.message || 'Fejl', 'error');
      }
    }
  };

  const deleteLocation = async (id) => {
    const ok = await confirm(t('toast.deleteLocation'));
    if (!ok) return;
    await api.deleteLocation(id);
    setLocations(prev => prev.filter(l => l.id !== id));
  };

  const moveLocation = async (type, index, direction) => {
    const locs = locations.filter(l => l.list_type === type);
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= locs.length) return;
    const reordered = [...locs];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    for (let i = 0; i < reordered.length; i++) {
      await api.updateLocation(reordered[i].id, { position: i, name: reordered[i].name });
    }
    setLocations(prev => [...prev.filter(l => l.list_type !== type), ...reordered.map((l, i) => ({ ...l, position: i }))]);
  };

  const createHousehold = async () => {
    if (!newHhName.trim()) return;
    try {
      const hh = await api.createHousehold({ name: newHhName.trim(), icon: newHhIcon });
      addHousehold(hh);
      await reloadHouseholds();
      setNewHhName(''); flash(t('toast.homeCreated'));
    } catch (err) { flash(err.message, 'error'); }
  };

  const joinHousehold = async () => {
    if (!joinCode.trim()) return;
    try {
      const hh = await api.joinHousehold(joinCode.trim().toUpperCase());
      addHousehold(hh);
      setJoinCode(''); flash(t('toast.joined'));
    } catch (err) { flash(err.message, 'error'); }
  };

  const removeMember = async (hhId, userId, memberName) => {
    const ok = await confirm(t('toast.removeMember', { name: memberName }));
    if (!ok) return;
    try {
      await api.removeMember(hhId, userId);
      setMembersMap(prev => ({
        ...prev,
        [hhId]: (prev[hhId] || []).filter(m => m.id !== userId)
      }));
      flash(t('toast.memberRemoved'));
    } catch (err) { flash(err.message, 'error'); }
  };

  const { filters, updateFilters } = usePreferences();

  const toggleFilter = (key) => {
    const next = filters.includes(key) ? filters.filter(f => f !== key) : [...filters, key];
    updateFilters(next);
  };

  const isAdmin = activeHousehold?.role === 'admin';
  const icons = ['🏠','🏡','🏘','🏕','🛖','🏰'];
  const allTabs = [['general', t('settings.tabs.general')],['households', t('settings.tabs.households')],['locations', t('settings.tabs.locations')],['diet', t('settings.tabs.diet')],['advanced', t('settings.tabs.advanced')]];

  const createToken = async () => {
    if (!newTokenName.trim()) return;
    try {
      const result = await api.createToken(newTokenName.trim());
      setCreatedToken(result);
      setNewTokenName('');
      loadTokens();
    } catch (err) { flash(err.message || 'Fejl ved oprettelse', 'error'); }
  };

  const deleteToken = async (id, name) => {
    const ok = await confirm(t('toast.deleteToken', { name }));
    if (!ok) return;
    await api.deleteToken(id);
    setTokens(prev => prev.filter(t => t.id !== id));
  };

  const saveServerUrl = () => {
    setServerUrl(serverUrlInput.trim() || null);
    flash(t('toast.serverSaved'));
  };

  const resetServerUrl = () => {
    setServerUrlInput('');
    setServerUrl(null);
    flash(t('toast.serverReset'));
  };

  return (
    <div className="flex flex-col h-screen" style={{ background: 'var(--bg-base)' }}>
      {ConfirmDialog}
      <div className="pt-safe px-4 pb-2" style={{ background: 'var(--bg-card)', borderBottom: '1px solid var(--border-subtle)' }}>
        <h1 className="ft-screen-title pt-3">⚙️ {t('settings.title')}</h1>

        {activeHousehold && (
          <button
            onClick={() => setTab('households')}
            style={{ marginTop: 8, marginBottom: 4, width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-elevated)', borderRadius: 12, padding: '10px 12px', border: 'none', cursor: 'pointer' }}
          >
            <span style={{ fontSize: 20 }}>{activeHousehold.icon}</span>
            <div style={{ flex: 1, textAlign: 'left' }}>
              <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>{activeHousehold.name}</p>
              <p style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>{t('settings.activeHome')}</p>
            </div>
            <span style={{ color: 'var(--fg-4)', fontSize: 14 }}>›</span>
          </button>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 4, overflowX: 'auto', paddingBottom: 4 }}>
          {allTabs.map(([tabKey, tabLabel]) => (
            <button key={tabKey} type="button" onClick={() => setTab(tabKey)}
              style={{
                padding: '6px 14px', borderRadius: 10, fontSize: 13, fontWeight: 500,
                whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                background: tab === tabKey ? 'var(--brand)' : 'var(--bg-elevated)',
                color: tab === tabKey ? '#fff' : 'var(--fg-2)',
              }}>{tabLabel}</button>
          ))}
        </div>
      </div>

      {msg && (
        <div style={{ margin: '12px 16px 0', padding: '10px 14px', borderRadius: 12, fontSize: 13, fontFamily: 'DM Sans, sans-serif', background: msgType === 'ok' ? 'oklch(55% 0.19 152 / 15%)' : 'oklch(55% 0.22 25 / 15%)', color: msgType === 'ok' ? 'var(--status-fresh)' : 'var(--status-expired)' }}>
          {msg}
        </div>
      )}

      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}

      <div ref={scrollRef} className="flex-1 overflow-y-scroll p-4" style={{ display: 'flex', flexDirection: 'column', gap: 12, WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(100px + env(safe-area-inset-bottom))' }}>

        {/* ── Generelt ─────────────────────────────────────────────── */}
        {tab === 'general' && (<>
          <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.profile')}</h3>
            <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>{user?.email}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={displayName} onChange={e => setDisplayName(e.target.value)}
                className="input-field" style={{ flex: 1 }}
                placeholder={t('settings.namePlaceholder')} />
              <button type="button" onClick={saveDisplayName}
                style={{ padding: '10px 16px', background: 'var(--brand)', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>{t('common.save')}</button>
            </div>
          </section>

          <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.appearance')}</h3>

            {/* Tema toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                  {theme === 'dark' ? t('settings.themeDarkLabel') : t('settings.themeLightLabel')}
                </p>
                <p style={{ fontSize: 12, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                  {theme === 'dark' ? t('settings.themeSwitchToLight') : t('settings.themeSwitchToDark')}
                </p>
              </div>
              <div onClick={toggleTheme} style={{ width: 50, height: 30, borderRadius: 999, cursor: 'pointer', background: theme === 'dark' ? 'oklch(60% 0.17 230)' : 'var(--fg-4)', position: 'relative', transition: 'background 200ms', flexShrink: 0 }}>
                <div style={{ width: 26, height: 26, background: 'white', borderRadius: '50%', position: 'absolute', top: 2, left: theme === 'dark' ? 22 : 2, boxShadow: '0 1px 4px oklch(0% 0 0 / 30%)', transition: 'left 200ms' }} />
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--border-subtle)' }} />

            {/* App-ikon */}
            <div>
              <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif', marginBottom: 10 }}>{t('settings.appIcon')}</p>
              <div style={{ display: 'flex', gap: 12 }}>
                {Object.entries(appIcons).map(([key, ic]) => (
                  <button key={key} type="button" onClick={() => setIcon(key)}
                    style={{ flex: 1, padding: '10px 8px', borderRadius: 14, border: 'none', background: iconKey === key ? `${ic.bg}20` : 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, outline: iconKey === key ? `2px solid ${ic.bg}` : 'none', transition: 'all 150ms' }}>
                    <AppIcon size={48} iconKey={key} />
                    <span style={{ fontSize: 12, color: 'var(--fg-2)', fontFamily: 'DM Sans, sans-serif', fontWeight: iconKey === key ? 600 : 400 }}>{t('settings.icon' + key[0].toUpperCase() + key.slice(1))}</span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ height: 1, background: 'var(--border-subtle)' }} />

            {/* Sprogvalg */}
            <div>
              <p style={{ fontSize: 15, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif', marginBottom: 8 }}>{t('settings.language')}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {[['da','dk','Dansk'],['en','gb','English'],['de','de','Deutsch'],['sv','se','Svenska'],['no','no','Norsk'],['nl','nl','Nederlands'],['fr','fr','Français'],['es','es','Español'],['fi','fi','Suomi']].map(([code, country, label]) => (
                  <button key={code} type="button" onClick={() => changeLanguage(code)}
                    style={{ padding: '10px 8px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 500, background: i18n.language === code ? 'var(--brand)' : 'var(--bg-elevated)', color: i18n.language === code ? '#fff' : 'var(--fg-2)', transition: 'all 150ms', textAlign: 'center' }}>
                    <img src={`https://flagcdn.com/32x24/${country}.png`} alt={label} style={{ width: 28, height: 21, marginBottom: 4, borderRadius: 2, display: 'block', margin: '0 auto 4px' }} />
                    <div style={{ fontSize: 11 }}>{label}</div>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Premium */}
          <section style={{ background: isPremium ? 'oklch(55% 0.17 250 / 10%)' : 'var(--bg-card)', borderRadius: 16, padding: 16, border: isPremium ? '1px solid oklch(55% 0.17 250 / 30%)' : 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif' }}>⭐ Premium</h3>
              {isPremium
                ? <span style={{ fontSize: 11, background: 'oklch(55% 0.17 250 / 20%)', color: 'oklch(60% 0.17 250)', padding: '3px 10px', borderRadius: 999, fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>{t('settings.premiumActive')}</span>
                : <button type="button" onClick={() => setShowPaywall(true)}
                    style={{ fontSize: 13, background: 'var(--brand)', color: 'white', border: 'none', borderRadius: 10, padding: '6px 14px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>
                    {t('settings.premiumUpgrade')}
                  </button>
              }
            </div>
            {isPremium ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <p style={{ fontSize: 13, color: 'var(--fg-2)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.premiumUnlimited')}</p>
                <p style={{ fontSize: 13, color: 'var(--fg-2)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.premiumAllFeatures')}</p>
                {import.meta.env.DEV && (
                  <button type="button" onClick={deactivatePremium}
                    style={{ fontSize: 12, color: 'var(--status-expired)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textAlign: 'left', padding: 0, marginTop: 4 }}>
                    [Dev] Deaktiver premium
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <p style={{ fontSize: 13, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.premiumFreeDesc')}</p>
                <p style={{ fontSize: 13, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.premiumPaidDesc')}</p>
              </div>
            )}
          </section>

          <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.passwordSection')}</h3>
            {!showChangePw ? (
              <button type="button" onClick={() => setShowChangePw(true)} className="ft-btn-primary">{t('settings.changePassword')}</button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)}
                  className="input-field" placeholder={t('settings.currentPassword')} autoComplete="current-password" />
                <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)}
                  className="input-field" placeholder={t('settings.newPassword')} autoComplete="new-password" />
                <input type="password" value={newPw2} onChange={e => setNewPw2(e.target.value)}
                  className="input-field" placeholder={t('settings.repeatPassword')} autoComplete="new-password" />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => { setShowChangePw(false); setCurrentPw(''); setNewPw(''); setNewPw2(''); }}
                    style={{ flex: 1, padding: '10px', background: 'var(--bg-elevated)', border: 'none', borderRadius: 10, cursor: 'pointer', color: 'var(--fg-2)', fontFamily: 'DM Sans, sans-serif' }}>{t('common.cancel')}</button>
                  <button type="button" onClick={changePassword}
                    style={{ flex: 1, padding: '10px', background: 'var(--brand)', border: 'none', borderRadius: 10, cursor: 'pointer', color: 'white', fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>{t('common.save')}</button>
                </div>
              </div>
            )}
          </section>

          <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.deleteAccountSection')}</h3>
            {!showDeleteAccount ? (
              <button type="button" onClick={() => setShowDeleteAccount(true)}
                style={{ padding: '10px', background: 'none', border: '1px solid var(--status-expired)', borderRadius: 10, cursor: 'pointer', color: 'var(--status-expired)', fontFamily: 'DM Sans, sans-serif', fontSize: 14 }}>
                {t('settings.deleteAccountBtn')}
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p style={{ fontSize: 13, color: 'var(--fg-3)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                  {t('settings.deleteAccountConfirm')}
                </p>
                <input type="password" value={deletePw} onChange={e => setDeletePw(e.target.value)}
                  className="input-field" placeholder={t('settings.deleteAccountPw')} autoFocus />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => { setShowDeleteAccount(false); setDeletePw(''); }}
                    style={{ flex: 1, padding: '10px', background: 'var(--bg-elevated)', border: 'none', borderRadius: 10, cursor: 'pointer', color: 'var(--fg-2)', fontFamily: 'DM Sans, sans-serif' }}>{t('common.cancel')}</button>
                  <button type="button" onClick={deleteAccount} disabled={deleting}
                    style={{ flex: 1, padding: '10px', background: 'var(--status-expired)', border: 'none', borderRadius: 10, cursor: 'pointer', color: 'white', fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>
                    {deleting ? t('settings.deleting') : t('settings.deleteAccount')}
                  </button>
                </div>
              </div>
            )}
          </section>

          <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.pushSection')}</h3>
            {!pushSupported ? (
              <p style={{ fontSize: 13, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.pushNotSupported')}</p>
            ) : (isNative ? !!nativeToken : !!pushSub) ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ fontSize: 13, color: 'var(--status-fresh)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.pushEnabled')}</p>
                <button type="button" onClick={unsubscribePush} style={{ fontSize: 13, color: 'var(--status-expired)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>{t('settings.pushDisable')}</button>
              </div>
            ) : (
              <button type="button" onClick={subscribePush} className="ft-btn-primary">{t('settings.pushEnable')}</button>
            )}
          </section>

          <button type="button" onClick={onLogout}
            style={{ width: '100%', padding: '14px', background: 'oklch(55% 0.22 25 / 10%)', color: 'var(--status-expired)', border: '1px solid oklch(55% 0.22 25 / 25%)', borderRadius: 16, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            {t('settings.logout')}
          </button>
        </>)}

        {/* ── Mine hjem ─────────────────────────────────────────────── */}
        {tab === 'households' && (<>
          {households.map(hh => (
            <div key={hh.id} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 14, border: hh.id === activeHousehold?.id ? '1px solid var(--brand)' : 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{hh.icon}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>{hh.name}</p>
                  <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>{hh.role === 'admin' ? t('settings.admin') : t('settings.member')}</p>
                </div>
                {hh.id === activeHousehold?.id
                  ? <span style={{ fontSize: 11, background: 'oklch(55% 0.17 250 / 20%)', color: 'oklch(60% 0.17 250)', padding: '3px 10px', borderRadius: 999 }}>{t('settings.premiumActive')}</span>
                  : <button type="button" onClick={() => switchHousehold(hh.id)}
                      style={{ fontSize: 13, background: 'var(--bg-elevated)', color: 'var(--fg-2)', border: 'none', borderRadius: 10, padding: '6px 12px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                      {t('settings.switchTo')}
                    </button>
                }
              </div>
              {hh.role === 'admin' && (
                <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>{t('settings.inviteCode')}</p>
                    <p style={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.1em', fontSize: 14, color: 'var(--fg-1)', margin: 0 }}>{hh.invite_code}</p>
                  </div>
                  <button type="button" onClick={async () => { await api.regenerateCode(hh.id); reloadHouseholds(); flash(t('toast.codeGenerated')); }}
                    style={{ fontSize: 13, color: 'oklch(60% 0.17 250)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>{t('settings.newCode')}</button>
                </div>
              )}
              <div style={{ background: 'var(--bg-elevated)', borderRadius: 12, padding: '10px 12px' }}>
                <p style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: '0 0 2px' }}>{t('settings.householdId')}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--fg-2)', margin: 0, wordBreak: 'break-all', userSelect: 'all' }}>{hh.id}</p>
              </div>
              {hh.role === 'admin' && (membersMap[hh.id] || []).length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <p style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '6px 0 4px' }}>
                    {t('settings.members')} ({(membersMap[hh.id] || []).length})
                  </p>
                  {(membersMap[hh.id] || []).map(m => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--fg-2)', flexShrink: 0 }}>
                        {(m.display_name || m.email)?.[0]?.toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                          {m.display_name || m.email}
                          {m.id === user?.id && <span style={{ color: 'oklch(60% 0.17 250)', marginLeft: 4, fontWeight: 400, fontSize: 12 }}>{t('settings.you')}</span>}
                        </p>
                        <p style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>{m.role === 'admin' ? t('settings.admin') : t('settings.member')}</p>
                      </div>
                      {m.id !== user?.id && (
                        <button onClick={() => removeMember(hh.id, m.id, m.display_name || m.email)}
                          style={{ fontSize: 12, background: 'oklch(55% 0.22 25 / 10%)', color: 'var(--status-expired)', border: '1px solid oklch(55% 0.22 25 / 25%)', padding: '4px 10px', borderRadius: 8, cursor: 'pointer', flexShrink: 0 }}>
                          {t('settings.remove')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.householdsCreate')}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {icons.map(i => (
                <button key={i} type="button" onClick={() => setNewHhIcon(i)}
                  style={{ width: 40, height: 40, fontSize: 20, borderRadius: 12, border: 'none', cursor: 'pointer', background: newHhIcon === i ? 'var(--brand)' : 'var(--bg-elevated)' }}>{i}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newHhName} onChange={e => setNewHhName(e.target.value)}
                className="input-field" style={{ flex: 1 }} placeholder={t('settings.householdsHomeName')} />
              <button type="button" onClick={createHousehold}
                style={{ padding: '10px 16px', background: 'var(--brand)', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>{t('settings.householdsCreate2')}</button>
            </div>
          </div>

          <div style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.householdsJoin')}</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
                className="input-field" style={{ flex: 1, fontFamily: 'monospace', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                placeholder="ABCD1234" maxLength={8} />
              <button type="button" onClick={joinHousehold}
                style={{ padding: '10px 16px', background: 'var(--status-fresh)', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>{t('settings.householdsJoinBtn')}</button>
            </div>
          </div>
        </>)}

        {/* ── Placeringer ───────────────────────────────────────────── */}
        {tab === 'locations' && (<>
          {[['freezer','❄️', t('settings.freezers')],['fridge','🥛', t('settings.fridges')],['pantry','🥫', t('settings.pantries')]].map(([type, icon, label]) => {
            const typeUnits = storageUnits.filter(u => u.list_type === type);
            return (
              <div key={type} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: '14px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>{icon} {label}</h3>
                  <span style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.unitCount', { count: typeUnits.length })}</span>
                </div>
                {typeUnits.map((unit, idx) => (
                  <div key={unit.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: 20 }}>{unit.icon || icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif' }}>{unit.name}</span>
                        {idx === 0 && (
                          <span style={{ fontSize: 10, background: 'var(--bg-sunken)', color: 'var(--fg-4)', padding: '1px 6px', borderRadius: 999, fontFamily: 'DM Sans, sans-serif' }}>{t('settings.primary')}</span>
                        )}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>{unit.location_count || 0} {t('settings.drawers')}</span>
                    </div>
                    {idx > 0 && (
                      <button type="button" onClick={() => deleteStorageUnit(unit)}
                        style={{ fontSize: 13, fontWeight: 500, background: 'oklch(55% 0.22 25 / 10%)', border: '1px solid oklch(55% 0.22 25 / 25%)', borderRadius: 8, cursor: 'pointer', color: 'var(--status-expired)', padding: '4px 10px', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.deleteUnit')}</button>
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <input
                    value={newUnit.list_type === type ? newUnit.name : ''}
                    onChange={e => setNewUnit({ name: e.target.value, list_type: type, icon: '' })}
                    className="input-field" style={{ fontSize: 13, padding: '8px 12px' }}
                    placeholder={`${t('common.add')} ${label.toLowerCase()}…`}
                  />
                  <button type="button" onClick={createStorageUnit}
                    disabled={!newUnit.name || newUnit.list_type !== type}
                    style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: 'var(--brand)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!newUnit.name || newUnit.list_type !== type) ? 0.4 : 1, flexShrink: 0 }}>
                    + {t('common.add')}
                  </button>
                </div>
                {!isPremium && typeUnits.length >= 1 && (
                  <p style={{ fontSize: 11, color: 'var(--status-warning)', marginTop: 6, fontFamily: 'DM Sans, sans-serif' }}>{t('settings.premiumRequired')}</p>
                )}
              </div>
            );
          })}

          <div style={{ height: 1, background: 'var(--border-subtle)', margin: '4px 0 12px' }} />

          {storageUnits.sort((a,b) => a.list_type.localeCompare(b.list_type) || a.position - b.position).map(unit => {
            const unitLocs = locations.filter(l => l.storage_unit_id === unit.id).sort((a,b) => a.position - b.position);
            const unitIcon = unit.list_type === 'freezer' ? '❄️' : unit.list_type === 'fridge' ? '🥛' : '🥫';
            const isActive = newLoc.storage_unit_id === unit.id;
            return (
              <div key={unit.id} style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 14, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: unit.position > 0 && !isPremium ? 'var(--fg-4)' : 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>
                    {unitIcon} {unit.name}
                  </h3>
                  {unit.position > 0 && !isPremium && (
                    <span onClick={() => setShowPaywall(true)}
                      style={{ fontSize: 11, background: 'oklch(64% 0.14 50 / 15%)', color: 'oklch(64% 0.14 50)', padding: '3px 8px', borderRadius: 999, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 500 }}>
                      {t('settings.premiumLocked')}
                    </span>
                  )}
                </div>
                {unitLocs.length === 0 && (
                  <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', marginBottom: 8 }}>{t('settings.noDrawersYet')}</p>
                )}
                {unitLocs.map((loc, idx) => (
                  <div key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      <button type="button" onClick={() => moveLocation(unit.list_type, locations.filter(l=>l.storage_unit_id===unit.id).findIndex(l=>l.id===loc.id), -1)} disabled={idx === 0}
                        style={{ fontSize: 10, background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: 'var(--fg-4)', opacity: idx === 0 ? 0.2 : 1, lineHeight: 1, padding: '1px 0' }}>▲</button>
                      <button type="button" onClick={() => moveLocation(unit.list_type, locations.filter(l=>l.storage_unit_id===unit.id).findIndex(l=>l.id===loc.id), 1)} disabled={idx === unitLocs.length - 1}
                        style={{ fontSize: 10, background: 'none', border: 'none', cursor: idx === unitLocs.length-1 ? 'default' : 'pointer', color: 'var(--fg-4)', opacity: idx === unitLocs.length-1 ? 0.2 : 1, lineHeight: 1, padding: '1px 0' }}>▼</button>
                    </div>
                    <span style={{ flex: 1, fontSize: 14, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif' }}>{loc.name}</span>
                    <button type="button" onClick={() => deleteLocation(loc.id)}
                      style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--status-expired)', padding: '0 4px', lineHeight: 1 }}>×</button>
                  </div>
                ))}
                {unit.position > 0 && !isPremium ? (
                  <div onClick={() => setShowPaywall(true)}
                    style={{ marginTop: 10, padding: '10px', borderRadius: 10, border: '1px dashed oklch(64% 0.14 50 / 30%)', textAlign: 'center', cursor: 'pointer', fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>
                    {t('settings.premiumUpgradeDrawers')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <input
                      value={isActive ? newLoc.name : ''}
                      onChange={e => setNewLoc({ name: e.target.value, list_type: unit.list_type, storage_unit_id: unit.id })}
                      onFocus={() => setNewLoc(prev => ({ ...prev, list_type: unit.list_type, storage_unit_id: unit.id }))}
                      className="input-field" style={{ fontSize: 13, padding: '8px 12px' }}
                      placeholder={t('settings.addDrawer')}
                    />
                    <button type="button" onClick={addLocation} disabled={!isActive || !newLoc.name}
                      style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: 'var(--brand)', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0, opacity: (!isActive || !newLoc.name) ? 0.4 : 1 }}>+</button>
                  </div>
                )}
              </div>
            );
          })}
        </>)}

        {/* ── Kostvaner ────────────────────────────────────────────── */}
        {tab === 'diet' && (() => {
          const DIET_OPTIONS = [
            { key: 'gluten-free',  emoji: '🌾' },
            { key: 'milk-free',    emoji: '🥛' },
            { key: 'lactose-free', emoji: '🫙' },
            { key: 'nut-free',     emoji: '🥜' },
            { key: 'vegetarian',   emoji: '🥕' },
            { key: 'vegan',        emoji: '🌿' },
          ];
          return (<>
            <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif', marginBottom: 8 }}>
                {t('settings.dietTitle')}
              </h3>
              <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', marginBottom: 12 }}>
                {t('settings.dietDesc')}
              </p>
              {DIET_OPTIONS.map(opt => (
                <div key={opt.key} onClick={() => toggleFilter(opt.key)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--bg-elevated)', cursor: 'pointer' }}>
                  <span style={{ fontSize: 22 }}>{opt.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fg-1)' }}>{t(`settings.diet.${opt.key}`)}</div>
                    <div style={{ fontSize: 12, color: 'var(--fg-4)' }}>{t(`settings.dietDesc_${opt.key}`)}</div>
                  </div>
                  <div style={{
                    width: 44, height: 26, borderRadius: 13, transition: 'background 0.2s',
                    background: filters.includes(opt.key) ? 'var(--brand)' : 'var(--bg-elevated)',
                    position: 'relative', flexShrink: 0,
                  }}>
                    <div style={{
                      position: 'absolute', top: 3, borderRadius: '50%', width: 20, height: 20,
                      background: 'white', transition: 'left 0.2s',
                      left: filters.includes(opt.key) ? 21 : 3,
                    }} />
                  </div>
                </div>
              ))}
            </section>
          </>);
        })()}

        {/* ── Avanceret ────────────────────────────────────────────── */}
        {tab === 'advanced' && (<>

          {/* Server URL — premium i native app */}
          <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>
              {t('settings.serverSection')}
            </h3>
            <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
              {t('settings.serverCurrent')} <strong>{getServerUrl()}</strong>
            </p>

            {isNative && !isPremium ? (
              <div onClick={() => setShowPaywall(true)} style={{ padding: 14, borderRadius: 12, border: '1px dashed oklch(64% 0.14 50 / 30%)', textAlign: 'center', cursor: 'pointer', fontSize: 13, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>
                {t('settings.serverPremiumRequired')}
              </div>
            ) : (
              <>
                <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                  {t('settings.serverDesc')}
                </p>
                <input
                  value={serverUrlInput}
                  onChange={e => setServerUrlInput(e.target.value)}
                  className="input-field"
                  placeholder="https://din-server.dk"
                  type="url"
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={saveServerUrl}
                    style={{ flex: 1, padding: '10px 0', background: 'var(--brand)', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                    {t('settings.serverSave')}
                  </button>
                  {serverUrlInput && (
                    <button type="button" onClick={resetServerUrl}
                      style={{ padding: '10px 14px', background: 'var(--bg-elevated)', color: 'var(--fg-2)', border: 'none', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                      {t('settings.serverReset')}
                    </button>
                  )}
                </div>
              </>
            )}
          </section>

          {/* PAT tokens */}
          <section style={{ background: 'var(--bg-card)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'DM Sans, sans-serif' }}>
              {t('settings.tokensSection')}
            </h3>
            <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
              {t('settings.tokensDesc')}
            </p>

            {/* Ny token */}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={newTokenName}
                onChange={e => setNewTokenName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createToken()}
                className="input-field"
                placeholder={t('settings.tokenNamePlaceholder')}
                style={{ flex: 1 }}
              />
              <button type="button" onClick={createToken} disabled={!newTokenName.trim()}
                style={{ padding: '10px 14px', background: 'var(--brand)', color: 'white', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: !newTokenName.trim() ? 0.4 : 1, flexShrink: 0, fontFamily: 'DM Sans, sans-serif' }}>
                {t('settings.tokenCreate')}
              </button>
            </div>

            {/* Ny token — vis KUN én gang */}
            {createdToken && (
              <div style={{ background: 'oklch(55% 0.19 152 / 10%)', border: '1px solid oklch(55% 0.19 152 / 30%)', borderRadius: 12, padding: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--status-fresh)', fontFamily: 'DM Sans, sans-serif', marginBottom: 8 }}>
                  {t('settings.tokenCopyNotice')}
                </p>
                <div style={{ background: 'var(--bg-base)', borderRadius: 8, padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', color: 'var(--fg-1)', userSelect: 'all', marginBottom: 8 }}>
                  {createdToken.token}
                </div>
                <button type="button"
                  onClick={() => { navigator.clipboard?.writeText(createdToken.token); flash(t('toast.copied')); setCreatedToken(null); }}
                  style={{ width: '100%', padding: '8px 0', background: 'var(--brand)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
                  {t('settings.tokenCopyClose')}
                </button>
              </div>
            )}

            {/* Token liste */}
            {tokens.length === 0 && !createdToken && (
              <p style={{ fontSize: 12, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif' }}>{t('settings.tokenNone')}</p>
            )}
            {tokens.map(tok => (
              <div key={tok.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>{tok.name}</p>
                  <p style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'DM Sans, sans-serif', margin: 0 }}>
                    {tok.prefix}… · {t('settings.tokenCreated')} {new Date(tok.created_at).toLocaleDateString()}
                    {tok.last_used_at && ` · ${t('settings.tokenLastUsed')} ${new Date(tok.last_used_at).toLocaleDateString()}`}
                  </p>
                </div>
                <button type="button" onClick={() => deleteToken(tok.id, tok.name)}
                  style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--status-expired)', padding: '0 4px', lineHeight: 1 }}>×</button>
              </div>
            ))}
          </section>

        </>)}

        {/* Spacer so fixed nav bar doesn't cover bottom content */}
        <div aria-hidden="true" style={{ flexShrink: 0, height: 'calc(80px + env(safe-area-inset-bottom))' }} />

      </div>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}
